// Starter course templates an instructor can use to populate a brand-new
// course in one click instead of building everything from a blank editor.
// Each template maps directly onto the real course/section/lecture/quiz
// shapes the backend expects (see backend/models/Course.js), so applying
// one is just: create the course, then create each section, then add each
// lecture (and its quiz, if any), then set the final quiz.
//
// `category` values match the ids in src/mocks/categories.js so the new
// course shows up correctly filtered on the public courses page.

// The two trade/HS templates below use `type: 'slides'` lectures whose
// images were sourced from an official ITC (International Trade Centre)
// e-learning deck and are served from this backend's own /uploads folder
// (backend/uploads/pre-slides/). Reuse the same API host the rest of the
// app already resolves (frontend/.env -> VITE_API_URL) so the images work
// against whichever backend this frontend is actually pointed at.
import { API_HOST } from '../api/apiServices';
const preSlide = (filename) => `${API_HOST}/uploads/pre-slides/${filename}`;

// A course generated from an instructor-uploaded document (see
// dcicFoundationClassTemplate.js's own header comment) rather than
// hand-written here -- included the same way so it shows up in the
// Course Template Gallery alongside the rest.
import { dcicFoundationClassTemplate } from './dcicFoundationClassTemplate';

export const courseTemplates = [
  {
    id: 'web-dev-fundamentals',
    title: 'Web Development Fundamentals',
    description: 'A hands-on introduction to HTML, CSS, and JavaScript for absolute beginners who want to build real web pages.',
    longDescription:
      "This course takes you from zero to building your first interactive web pages. You'll start with HTML structure, move into CSS styling and layout, and finish with enough JavaScript to make a page respond to user actions. Every lecture is written to be practical -- you'll understand not just the syntax, but why the web works the way it does.",
    category: 'web-dev',
    level: 'Beginner',
    duration: '4 weeks',
    isPaid: false,
    price: 0,
    thumbnail: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1000&q=60',
    whatYouLearn: [
      'How the web works: browsers, servers, and requests',
      'Structure any page correctly with semantic HTML',
      'Style and lay out pages with modern CSS, including Flexbox',
      'Add interactivity with core JavaScript and the DOM',
    ],
    requirements: [
      'A computer with any modern web browser installed',
      'No prior coding experience required',
    ],
    tags: ['html', 'css', 'javascript', 'web development', 'beginner'],
    sections: [
      {
        title: 'HTML Foundations',
        lectures: [
          {
            title: 'What Is HTML and How the Web Works',
            type: 'text',
            duration: 360,
            textContent:
`Every website you have ever visited is built on HTML (HyperText Markup Language). HTML doesn't make a page look pretty -- that's CSS's job. Instead, HTML describes the *structure* and *meaning* of content: this is a heading, this is a paragraph, this is a list, this is a button.

When you type a web address into your browser, your browser sends a request to a server somewhere in the world. That server sends back an HTML file (plus CSS and JavaScript files). Your browser reads the HTML from top to bottom and builds what's called the DOM (Document Object Model) -- a tree-like structure it can display and later let JavaScript change.

An HTML element is written as a pair of tags: an opening tag like <p> and a closing tag like </p>, with content in between. Some elements, like images, are "self-closing" because they don't wrap around content: <img src="photo.jpg">.

Every HTML document starts with the same basic skeleton:

<!DOCTYPE html>
<html>
  <head>
    <title>My First Page</title>
  </head>
  <body>
    <h1>Hello, world!</h1>
  </body>
</html>

The <!DOCTYPE html> line tells the browser this is a modern HTML document. Everything inside <head> is metadata about the page (like its title, which shows in the browser tab). Everything inside <body> is what actually shows up on the page.`,
          },
          {
            title: 'Structuring a Page with HTML Elements',
            type: 'text',
            duration: 480,
            textContent:
`Now that you know the basic skeleton, let's look at the elements you'll use constantly.

Headings and paragraphs: HTML gives you six heading levels, <h1> through <h6>, from most to least important. Use exactly one <h1> per page for the main title, and use the other levels to organize sections underneath it. Regular text goes inside <p> tags.

Links and images: A link is an <a> tag with an href attribute pointing to where it should go: <a href="https://example.com">Visit Example</a>. An image is an <img> tag with a src attribute pointing to the image file, and it should always have an alt attribute describing the image for screen readers and for when the image fails to load: <img src="cat.jpg" alt="A sleeping cat">.

Lists: Use <ul> for an unordered (bulleted) list and <ol> for an ordered (numbered) list. Each item inside goes in an <li> tag:

<ul>
  <li>Milk</li>
  <li>Eggs</li>
  <li>Bread</li>
</ul>

Divs, spans, and semantic tags: A <div> is a generic block-level container with no meaning of its own -- useful for grouping things for styling. A <span> is the same idea but inline, for wrapping a small piece of text. Modern HTML also gives you semantic tags that describe the role of a section: <header>, <nav>, <main>, <section>, <article>, and <footer>. Using these instead of plain <div>s everywhere makes your page easier for browsers, search engines, and screen readers to understand.

A good habit from day one: write valid, properly nested HTML (every tag you open, you close, in the right order) and use semantic tags where they fit. It costs nothing extra and saves you real debugging time later.`,
            quiz: {
              title: 'HTML Foundations Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'What does HTML primarily describe about a web page?',
                  options: [
                    'Its structure and meaning',
                    'Its colors and fonts',
                    'Its server-side logic',
                    'Its database connections',
                  ],
                  correctAnswer: 0,
                  explanation: 'HTML describes structure and meaning; CSS handles appearance and JavaScript handles behavior.',
                },
                {
                  questionText: 'Which attribute should every <img> tag include for accessibility?',
                  options: ['title', 'alt', 'href', 'target'],
                  correctAnswer: 1,
                  explanation: 'The alt attribute describes the image for screen readers and shows if the image fails to load.',
                },
                {
                  questionText: 'How many <h1> elements should a typical page have?',
                  options: ['As many as you want', 'Exactly one', 'Exactly six', 'Zero'],
                  correctAnswer: 1,
                  explanation: 'Convention (and accessibility best practice) is one <h1> per page as the main title.',
                },
              ],
            },
          },
        ],
      },
      {
        title: 'Styling with CSS',
        lectures: [
          {
            title: 'CSS Selectors and the Box Model',
            type: 'text',
            duration: 420,
            textContent:
`CSS (Cascading Style Sheets) controls how HTML looks. You write rules that select elements and describe how they should appear:

h1 {
  color: teal;
  font-size: 32px;
}

The part before the curly braces is the selector -- it decides which elements the rule applies to. You can select by tag name (h1), by class (.card selects any element with class="card"), or by id (#header selects the one element with id="header"). Classes are reusable across many elements; ids should be unique on a page.

Every element on a page is a rectangular box, and CSS's "box model" describes what makes up that box, from the inside out: content, padding (space inside the border), border, and margin (space outside the border). If you set width: 200px on an element, by default that's the width of the content only -- padding and border add to it. Many developers add this rule near the top of their stylesheet to make sizing more predictable:

* {
  box-sizing: border-box;
}

With box-sizing: border-box, the width and height you set include padding and border, which is usually what you actually want.

CSS rules "cascade" -- when multiple rules could apply to the same element, more specific selectors and rules that appear later generally win. This is why the language is called Cascading Style Sheets.`,
          },
          {
            title: 'Flexbox Layout Basics',
            type: 'text',
            duration: 480,
            textContent:
`Before Flexbox, lining up boxes side by side and making them share space evenly was surprisingly fiddly. Flexbox fixes that.

To use it, you set display: flex on a container. Every direct child of that container becomes a "flex item" that Flexbox will arrange for you:

.container {
  display: flex;
}

By default, flex items line up in a row, left to right. Two properties on the container do most of the work:

justify-content controls spacing along the main axis (horizontally, by default): try flex-start, center, space-between, or space-around.

align-items controls alignment along the cross axis (vertically, by default): try flex-start, center, or stretch.

.container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

On individual items, flex-grow lets an item expand to fill leftover space (flex-grow: 1 is common for "take up all remaining room"), and you can reverse or wrap the direction with flex-direction: column or flex-wrap: wrap.

A very common real-world pattern -- a navbar with a logo on the left and links on the right, all vertically centered -- is just:

nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

Flexbox is one-dimensional (a row or a column). For full two-dimensional grid layouts, CSS Grid is the tool to reach for, but Flexbox alone will cover the majority of everyday layout needs.`,
            quiz: {
              title: 'CSS Layout Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'With box-sizing: border-box, what does a set width include?',
                  options: [
                    'Content only',
                    'Content and padding only',
                    'Content, padding, and border',
                    'Margin only',
                  ],
                  correctAnswer: 2,
                  explanation: 'border-box makes width/height include content, padding, and border -- margin is always separate.',
                },
                {
                  questionText: 'Which CSS property turns a container into a flex container?',
                  options: ['flex-direction: row', 'display: flex', 'position: flex', 'align: flex'],
                  correctAnswer: 1,
                  explanation: 'display: flex on the parent makes its direct children flex items.',
                },
                {
                  questionText: 'Which property centers flex items along the cross axis?',
                  options: ['justify-content', 'align-items', 'text-align', 'flex-wrap'],
                  correctAnswer: 1,
                  explanation: 'align-items controls alignment on the cross axis (vertical, in a default row layout).',
                },
              ],
            },
          },
        ],
      },
      {
        title: 'JavaScript Essentials',
        lectures: [
          {
            title: 'Variables, Types, and Functions',
            type: 'text',
            duration: 480,
            textContent:
`JavaScript is the language that makes web pages interactive. You declare variables with let (for values that can change) or const (for values that shouldn't be reassigned) -- avoid the older var keyword in new code.

let score = 0;
const playerName = "Ada";

JavaScript's core types are: strings ("hello"), numbers (42, 3.14), booleans (true / false), arrays ([1, 2, 3]), objects ({ name: "Ada", age: 25 }), and two special values: null (intentionally empty) and undefined (not yet assigned).

Functions let you package up reusable logic:

function greet(name) {
  return "Hello, " + name + "!";
}

console.log(greet("Ada")); // "Hello, Ada!"

Modern JavaScript also has a shorter "arrow function" syntax, which is very common in real codebases:

const greet = (name) => {
  return "Hello, " + name + "!";
};

// or, for a single expression, even shorter:
const greet = (name) => "Hello, " + name + "!";

Conditionals (if / else) let your code make decisions, and loops (for, while) let it repeat work:

if (score > 100) {
  console.log("High score!");
} else {
  console.log("Keep going.");
}

for (let i = 0; i < 5; i++) {
  console.log(i);
}

Get comfortable with these building blocks -- variables, types, functions, conditionals, and loops -- and you have everything you need to start writing real logic.`,
          },
          {
            title: 'Making Pages Interactive with the DOM',
            type: 'text',
            duration: 480,
            textContent:
`The DOM (Document Object Model) is the browser's live, in-memory representation of your HTML. JavaScript can read it, change it, and react to what the user does on the page.

To find an element, the most common method is document.querySelector, which takes a CSS selector, exactly like the ones you used in your CSS file:

const button = document.querySelector("#submit-btn");
const items = document.querySelectorAll(".list-item"); // returns all matches

Once you have an element, you can change its content or styling from JavaScript:

button.textContent = "Click me!";
button.style.backgroundColor = "teal";
button.classList.add("active");

The real power comes from event listeners -- functions that run in response to something happening, like a click:

button.addEventListener("click", () => {
  alert("Button was clicked!");
});

A very common pattern is reading what the user typed into a form and using it:

const input = document.querySelector("#name-input");
const output = document.querySelector("#output");

button.addEventListener("click", () => {
  output.textContent = "Hello, " + input.value + "!";
});

This is the same three-step pattern behind almost every interactive feature on the web: select the element(s) you care about, listen for an event, and update the DOM in response. Once this pattern feels natural, you're ready to build genuinely interactive pages.`,
            quiz: {
              title: 'JavaScript Essentials Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'Which keyword should you use for a variable that will never be reassigned?',
                  options: ['var', 'let', 'const', 'static'],
                  correctAnswer: 2,
                  explanation: 'const declares a variable that cannot be reassigned after its initial value.',
                },
                {
                  questionText: 'What does document.querySelector do?',
                  options: [
                    'Creates a new HTML element',
                    'Finds the first element matching a CSS selector',
                    'Sends a network request',
                    'Deletes an element from the page',
                  ],
                  correctAnswer: 1,
                  explanation: 'querySelector returns the first element in the DOM that matches the given CSS selector.',
                },
                {
                  questionText: 'What is an event listener used for?',
                  options: [
                    'Styling elements',
                    'Running code in response to something happening, like a click',
                    'Declaring variables',
                    'Loading images faster',
                  ],
                  correctAnswer: 1,
                  explanation: 'addEventListener registers a function to run when a specific event (click, input, etc.) occurs.',
                },
              ],
            },
          },
        ],
      },
    ],
    finalQuiz: {
      title: 'Web Development Fundamentals -- Final Assessment',
      passingScore: 70,
      questions: [
        {
          questionText: 'Which language is responsible for a page\'s structure and meaning?',
          options: ['CSS', 'HTML', 'JavaScript', 'JSON'],
          correctAnswer: 1,
          explanation: 'HTML defines structure and meaning; CSS handles appearance and JavaScript handles behavior.',
        },
        {
          questionText: 'Which CSS display value turns on Flexbox for a container?',
          options: ['display: block', 'display: flex', 'display: grid', 'display: inline'],
          correctAnswer: 1,
          explanation: 'display: flex makes an element a flex container, with its direct children as flex items.',
        },
        {
          questionText: 'In JavaScript, which of these finds an element in the page?',
          options: ['document.querySelector()', 'window.alert()', 'Array.map()', 'JSON.parse()'],
          correctAnswer: 0,
          explanation: 'document.querySelector(selector) returns the first matching element in the DOM.',
        },
        {
          questionText: 'What is the DOM?',
          options: [
            'A CSS layout method',
            'The browser\'s in-memory representation of the page that JavaScript can read and change',
            'A JavaScript data type',
            'A type of HTTP request',
          ],
          correctAnswer: 1,
          explanation: 'The DOM (Document Object Model) is the live tree structure the browser builds from your HTML.',
        },
      ],
    },
  },

  {
    id: 'digital-marketing-essentials',
    title: 'Digital Marketing Essentials for Nigerian Businesses',
    description: 'A practical guide to reaching customers online -- social media, content, and paid ads -- built around real budgets and tools available in Nigeria.',
    longDescription:
      "Whether you're marketing a small shop, a service business, or your own brand, this course walks through the fundamentals of digital marketing with realistic examples: choosing platforms your customers actually use, planning content you can sustain, and spending ad budgets (even small ones) wisely. You'll finish with a simple marketing plan you can put to work immediately.",
    category: 'marketing',
    level: 'Beginner',
    duration: '3 weeks',
    isPaid: false,
    price: 0,
    thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1000&q=60',
    whatYouLearn: [
      'Define your target customer and core message',
      'Choose the right social platforms for your business',
      'Plan and stick to a content calendar',
      'Track the metrics that actually matter',
      'Run a small, well-targeted paid ad campaign',
    ],
    requirements: [
      'A business idea, product, or service to market (existing or planned)',
      'Access to a smartphone or computer with internet',
    ],
    tags: ['digital marketing', 'social media', 'small business', 'nigeria', 'advertising'],
    sections: [
      {
        title: 'Marketing Foundations',
        lectures: [
          {
            title: 'Understanding Your Target Customer',
            type: 'text',
            duration: 360,
            textContent:
`Good marketing starts before you write a single post -- it starts with knowing exactly who you're talking to. "Everyone" is not a target customer; it's a sign the message will be too vague to convince anyone.

Start by answering four questions as specifically as you can:

Who are they? Age range, location, income level, and daily life. "Young professionals in Lagos who commute by bus" is far more useful than "young people."

What problem do they have? People buy solutions to problems, not products. A customer isn't buying skincare, they're buying confidence or clear skin. Write down the problem in their words, not yours.

Where do they spend time online? A 45-year-old business owner and a 19-year-old student are rarely on the same platforms, at the same times, consuming the same kind of content.

What stops them from buying today? Price, trust, awareness that a solution exists, or simply not having heard of you yet. Your marketing message should directly address this.

Once you can describe your customer in a sentence or two -- "small restaurant owners in Port Harcourt who are losing money to food waste" -- every other marketing decision gets easier, because you can ask "would this actually reach and convince them?" instead of guessing.`,
          },
          {
            title: 'Building a Simple Marketing Plan',
            type: 'text',
            duration: 420,
            textContent:
`A marketing plan doesn't need to be a 40-page document. For a small or growing business, a useful plan fits on one page and answers five things:

1. Goal: What are you actually trying to achieve in the next 1-3 months? "More sales" is vague -- "50 new customers by the end of next month" is a goal you can plan around and measure.

2. Audience: The specific customer description from the previous lecture.

3. Message: The one core reason someone should choose you, in plain language. Not a list of ten features -- the single most convincing reason.

4. Channels: Where you'll actually show up -- pick 1-2 platforms to start, not five. It is far better to post consistently and well on one platform than to be inconsistent across five.

5. Budget and cadence: How much time or money you can realistically commit each week, and how often you'll post or run ads. A plan that assumes daily posting when you can only manage twice a week will fail -- plan around your real capacity.

Write these five things down. Review and adjust the plan every few weeks based on what's actually working -- not what you assumed would work when you wrote it. Marketing plans should be living documents, not fixed contracts.`,
            quiz: {
              title: 'Marketing Foundations Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'Why is "everyone" a weak target customer description?',
                  options: [
                    'It\'s too specific',
                    'It makes your message too vague to convince anyone in particular',
                    'It costs more to advertise to',
                    'It is not allowed on social media',
                  ],
                  correctAnswer: 1,
                  explanation: 'A vague audience leads to a vague message that resonates with no one in particular.',
                },
                {
                  questionText: 'What is the recommended number of channels to start with?',
                  options: ['As many as possible', '1-2', 'Exactly 5', 'None, focus on word of mouth only'],
                  correctAnswer: 1,
                  explanation: 'Starting with 1-2 channels lets you post consistently rather than spreading effort too thin.',
                },
              ],
            },
          },
        ],
      },
      {
        title: 'Social Media Marketing',
        lectures: [
          {
            title: 'Choosing the Right Platforms',
            type: 'text',
            duration: 360,
            textContent:
`Different platforms suit different businesses and content styles. Rather than trying to be everywhere, match the platform to your customer and the kind of content you can realistically produce.

Instagram and TikTok favor visual, short-form content -- strong choices for products, food, fashion, beauty, or anything that photographs or films well. WhatsApp Business/Status is extremely effective in Nigeria for direct relationships with existing customers, order-taking, and repeat business, especially for smaller or service-based businesses. Facebook still has broad reach across a wider age range and works well for community groups and local audiences. LinkedIn suits B2B services, consultants, and professional services rather than consumer products. Twitter/X is strong for real-time engagement, customer service, and building a personal or brand voice within a specific community or industry.

A practical way to choose: look at where your specific target customer (from the earlier lecture) already spends time, and pick the platform that matches the type of content you can consistently create -- photos, short videos, or written posts. It's better to master one platform than to be mediocre across four.`,
          },
          {
            title: 'Creating a Content Calendar',
            type: 'text',
            duration: 420,
            textContent:
`A content calendar is simply a plan for what you'll post and when, decided in advance instead of scrambling for an idea every morning. It solves the two most common reasons small business social media fails: inconsistency and repetitive content.

A simple structure that works for most small businesses is a repeating weekly theme, for example:
- Monday: Behind the scenes (how a product is made, a day in the business)
- Wednesday: Educational (a tip related to your product or industry)
- Friday: Customer spotlight or testimonial
- Sunday: Promotion or offer

Batch your content creation -- set aside one session per week (or every two weeks) to plan and create several posts at once, rather than starting from zero daily. This is far more sustainable than trying to be spontaneously creative every single day.

Keep a simple running list of content ideas whenever they come to you -- a customer question, a common misconception about your industry, a behind-the-scenes moment -- so you're never staring at a blank page when it's time to post.

Consistency beats perfection. A slightly-less-polished post published on schedule will outperform a perfect post that never gets made because you were waiting for the "right" idea.`,
            quiz: {
              title: 'Social Media Strategy Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'Which platform is highlighted as especially effective for direct customer relationships in Nigeria?',
                  options: ['LinkedIn', 'WhatsApp Business', 'Twitter/X', 'Pinterest'],
                  correctAnswer: 1,
                  explanation: 'WhatsApp Business/Status is widely used in Nigeria for direct, ongoing customer relationships.',
                },
                {
                  questionText: 'What problem does a content calendar mainly solve?',
                  options: [
                    'It guarantees viral posts',
                    'Inconsistency and repetitive content',
                    'It removes the need for good photos',
                    'It replaces the need for a target audience',
                  ],
                  correctAnswer: 1,
                  explanation: 'Planning ahead keeps posting consistent and varied instead of reactive and repetitive.',
                },
              ],
            },
          },
        ],
      },
      {
        title: 'Measuring What Matters',
        lectures: [
          {
            title: 'Key Metrics Every Marketer Should Track',
            type: 'text',
            duration: 360,
            textContent:
`It's easy to obsess over likes, but likes rarely pay bills. Focus on metrics that connect to your actual business goal:

Reach and impressions tell you how many people saw your content -- useful for awareness, but only a starting point.

Engagement rate (likes, comments, shares divided by reach) tells you whether your content actually resonates, not just whether it was seen.

Click-through rate (CTR) tells you how many people who saw your post or ad actually took the next step, like visiting a link.

Conversion rate tells you what percentage of those clicks turned into the outcome you wanted -- a sale, a booking, a sign-up. This is usually the number that matters most to the business.

Cost per acquisition (CPA), if you're running paid ads, tells you how much you spent to get one customer -- compare this to what that customer is actually worth to you.

Pick 2-3 metrics tied directly to your goal from the marketing plan lecture, and check them on a fixed schedule (weekly is usually enough for a small business) rather than refreshing constantly. Tracking everything is the same as tracking nothing -- it's overwhelming and doesn't drive decisions.`,
          },
          {
            title: 'Running Your First Paid Ad Campaign',
            type: 'text',
            duration: 480,
            textContent:
`You don't need a large budget to run a useful ad campaign -- even a small, well-targeted budget can teach you a lot and bring in real customers, as long as you set it up carefully.

Start with one clear objective: awareness (more people see your brand), traffic (more people visit your page or site), or conversions (more people take a specific action, like buying or messaging you). Most ad platforms ask you to choose an objective up front, and it changes how the algorithm optimizes your spend -- so pick the one that matches what you actually want.

Target narrowly at first. Use what you learned about your customer -- location, age range, and interests -- to keep your audience focused rather than broad. A tightly targeted small budget usually outperforms a broad audience with the same budget.

Test more than one version. Create at least two versions of your ad (different image, or different headline) and run them at the same time with a small budget split between them. This is called A/B testing, and it quickly shows you which message resonates before you commit a larger budget.

Set a small daily budget you're comfortable with, let it run for at least 3-5 days before judging results (platforms need time to optimize delivery), and then look back at the metrics from the previous lecture -- CTR and conversion rate especially -- to decide whether to keep running it, adjust it, or stop it.

Treat your first campaign as a learning exercise, not a make-or-break bet. The goal is to learn what messaging and targeting work for your specific audience, then reinvest in what's proven to work.`,
            quiz: {
              title: 'Measurement & Advertising Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'Which metric tells you what percentage of clicks turned into an actual sale or sign-up?',
                  options: ['Reach', 'Engagement rate', 'Conversion rate', 'Impressions'],
                  correctAnswer: 2,
                  explanation: 'Conversion rate measures how many people who clicked actually completed the desired action.',
                },
                {
                  questionText: 'What is A/B testing in the context of ads?',
                  options: [
                    'Running the same ad on two different days',
                    'Running two versions of an ad to see which performs better',
                    'Doubling your ad budget',
                    'Targeting two countries at once',
                  ],
                  correctAnswer: 1,
                  explanation: 'A/B testing compares two ad variations against each other to identify the stronger performer.',
                },
              ],
            },
          },
        ],
      },
    ],
    finalQuiz: {
      title: 'Digital Marketing Essentials -- Final Assessment',
      passingScore: 70,
      questions: [
        {
          questionText: 'What should you define before choosing where to market?',
          options: ['Your logo colors', 'Your specific target customer', 'Your office address', 'Your competitor\'s prices'],
          correctAnswer: 1,
          explanation: 'A clear target customer shapes every other marketing decision, including platform choice and messaging.',
        },
        {
          questionText: 'What is the main benefit of a content calendar?',
          options: ['Guaranteed virality', 'Consistency and variety in what you post', 'Lower ad costs', 'More followers overnight'],
          correctAnswer: 1,
          explanation: 'A content calendar keeps posting consistent and prevents last-minute scrambling for ideas.',
        },
        {
          questionText: 'Which metric usually matters most to the actual business outcome?',
          options: ['Likes', 'Conversion rate', 'Follower count', 'Number of hashtags used'],
          correctAnswer: 1,
          explanation: 'Conversion rate ties directly to whether marketing efforts produce the desired business result.',
        },
        {
          questionText: 'When starting paid ads, what targeting approach is recommended?',
          options: [
            'Target everyone to maximize reach',
            'Target narrowly based on your known customer',
            'Only target other businesses',
            'Skip targeting entirely',
          ],
          correctAnswer: 1,
          explanation: 'Narrow, well-informed targeting typically outperforms broad targeting on the same budget.',
        },
      ],
    },
  },

  {
    id: 'data-analysis-python',
    title: 'Introduction to Data Analysis with Python',
    description: 'Learn to load, clean, analyze, and visualize real data using Python and pandas -- a practical foundation for any data-driven role.',
    longDescription:
      "This course teaches the core workflow every data analyst uses: getting data into Python, cleaning it up, asking questions of it, and communicating what you find. Using pandas and matplotlib, you'll work through the same steps professionals use on messy, real-world datasets, building skills that transfer directly to reporting, research, or a first data analytics role.",
    category: 'data-science',
    level: 'Intermediate',
    duration: '5 weeks',
    isPaid: true,
    price: 15000,
    hasDiscount: false,
    discount: 0,
    thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1000&q=60',
    whatYouLearn: [
      'Load and explore datasets using pandas DataFrames',
      'Clean messy data: missing values, duplicates, and inconsistent formatting',
      'Group, aggregate, and summarize data to answer real questions',
      'Create clear charts with matplotlib',
      'Communicate findings in a way non-technical stakeholders understand',
    ],
    requirements: [
      'Basic Python syntax (variables, functions, loops)',
      'Python installed, or willingness to use a free notebook environment like Google Colab',
    ],
    tags: ['python', 'pandas', 'data analysis', 'data science', 'matplotlib'],
    sections: [
      {
        title: 'Python & Pandas Basics',
        lectures: [
          {
            title: 'Setting Up Your Data Analysis Environment',
            type: 'text',
            duration: 360,
            textContent:
`Before analyzing anything, you need an environment to work in. Two libraries do almost all the heavy lifting in this course: pandas, for working with tabular data, and matplotlib, for charts.

If you have Python installed locally, install both with:

pip install pandas matplotlib

If you'd rather not install anything, Google Colab (colab.research.google.com) gives you a free, browser-based notebook with pandas and matplotlib already installed -- a great way to follow along without any setup.

A "notebook" (Jupyter or Colab) lets you write and run small chunks of code (called cells) one at a time, seeing the output immediately below each cell -- ideal for data analysis, where you're constantly checking what your data looks like as you go, rather than running one long script from start to finish.

Start every analysis notebook the same way, by importing the libraries you'll use:

import pandas as pd
import matplotlib.pyplot as plt

The "pd" and "plt" nicknames are a near-universal convention -- you'll see them in almost every pandas tutorial and real-world project, so it's worth adopting them from the start.`,
          },
          {
            title: 'Loading and Exploring Data with Pandas',
            type: 'text',
            duration: 480,
            textContent:
`Pandas' core structure is the DataFrame -- think of it as a spreadsheet inside Python, with rows and named columns. You'll most often load data from a CSV file:

df = pd.read_csv("sales_data.csv")

Once loaded, a handful of methods answer most of your first questions about a new dataset:

df.head() shows the first 5 rows, so you can see what the data actually looks like.
df.shape tells you the number of (rows, columns).
df.columns lists every column name.
df.info() shows each column's data type and how many non-missing values it has -- often the fastest way to spot problems.
df.describe() gives summary statistics (mean, min, max, etc.) for numeric columns.

To look at a single column, use square brackets with the column name: df["price"]. To filter rows based on a condition, put the condition inside square brackets:

expensive = df[df["price"] > 1000]

This reads naturally: "give me the rows of df where price is greater than 1000." You can combine conditions with & (and) and | (or), just remember to wrap each condition in its own parentheses:

df[(df["price"] > 1000) & (df["category"] == "Electronics")]

Spending five minutes running head(), info(), and describe() on any new dataset before doing anything else will save you from building an entire analysis on a misunderstanding of your data.`,
            quiz: {
              title: 'Pandas Basics Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'What is a pandas DataFrame most similar to?',
                  options: ['A single number', 'A spreadsheet with rows and named columns', 'An image file', 'A web page'],
                  correctAnswer: 1,
                  explanation: 'A DataFrame is a table-like structure with labeled rows and columns, similar to a spreadsheet.',
                },
                {
                  questionText: 'Which method shows each column\'s data type and missing-value count?',
                  options: ['df.head()', 'df.describe()', 'df.info()', 'df.columns'],
                  correctAnswer: 2,
                  explanation: 'df.info() summarizes column types and non-null counts, making it useful for spotting data issues.',
                },
              ],
            },
          },
        ],
      },
      {
        title: 'Cleaning and Transforming Data',
        lectures: [
          {
            title: 'Handling Missing Values and Duplicates',
            type: 'text',
            duration: 420,
            textContent:
`Real-world data is rarely clean. Before analyzing anything meaningfully, you need to deal with gaps and repeats.

To find missing values, use:

df.isnull().sum()

This shows how many missing values exist in each column, which tells you where to focus.

You generally have two options for missing values: remove them, or fill them in.

df.dropna() removes any row that has at least one missing value -- simple, but can throw away a lot of otherwise-good data if used carelessly.

df["column"].fillna(value) replaces missing values in that column with something sensible -- often the column's average (df["column"].mean()) for numeric data, or a label like "Unknown" for categorical data.

Which approach is right depends on how much data is missing and why. A column that's 90% empty is often better dropped entirely; a column that's missing a handful of values is usually better filled in than losing those rows.

Duplicate rows are just as common, especially with data pulled from multiple sources or exports. Find and remove them with:

df.duplicated().sum()   # count them
df = df.drop_duplicates()  # remove them

Always check for missing values and duplicates as one of your very first steps -- analyzing data before cleaning it is one of the most common ways to arrive at confidently wrong conclusions.`,
          },
          {
            title: 'Grouping and Aggregating Data',
            type: 'text',
            duration: 480,
            textContent:
`Most real analysis questions are really "group by" questions in disguise: "What's the average sale per region?" "How many orders per month?" "Which product sold the most?" Pandas' groupby() answers exactly this kind of question.

The pattern is always: group by a category, then aggregate a numeric column:

df.groupby("region")["sales"].sum()

This reads as: "group the rows by region, then sum the sales column within each group." Swap sum() for mean(), count(), max(), or min() depending on the question you're answering.

You can group by more than one column at once, for finer-grained answers:

df.groupby(["region", "month"])["sales"].sum()

To get several statistics at once instead of just one, use .agg():

df.groupby("region")["sales"].agg(["sum", "mean", "count"])

This single line answers "what's the total, average, and number of sales in each region" all at once -- exactly the kind of summary you'd put in a report.

A practical tip: after grouping, sort the result to bring the most important rows to the top:

df.groupby("region")["sales"].sum().sort_values(ascending=False)

Getting comfortable with groupby() is one of the highest-leverage skills in this course -- a huge share of real analysis work is some variation of "group, then summarize."`,
            quiz: {
              title: 'Data Cleaning & Grouping Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'What does df.isnull().sum() show you?',
                  options: [
                    'The total number of rows',
                    'The count of missing values per column',
                    'The sum of every numeric column',
                    'A list of duplicate rows',
                  ],
                  correctAnswer: 1,
                  explanation: 'isnull() flags missing values and sum() counts them per column.',
                },
                {
                  questionText: 'What does df.groupby("region")["sales"].sum() calculate?',
                  options: [
                    'Total sales across the whole dataset',
                    'Total sales, summed separately for each region',
                    'The average of all columns',
                    'The number of regions in the data',
                  ],
                  correctAnswer: 1,
                  explanation: 'groupby splits the data by region, then sum() totals the sales column within each group.',
                },
              ],
            },
          },
        ],
      },
      {
        title: 'Visualizing and Communicating Insights',
        lectures: [
          {
            title: 'Creating Charts with Matplotlib',
            type: 'text',
            duration: 420,
            textContent:
`A number in a table rarely convinces anyone as quickly as a well-made chart. Matplotlib is the standard tool for turning pandas results into visuals.

A basic line chart, often used for trends over time:

plt.plot(df["month"], df["sales"])
plt.xlabel("Month")
plt.ylabel("Sales")
plt.title("Monthly Sales Trend")
plt.show()

A bar chart, ideal for comparing categories against each other:

sales_by_region = df.groupby("region")["sales"].sum()
sales_by_region.plot(kind="bar")
plt.title("Total Sales by Region")
plt.show()

Notice that pandas Series (the result of a groupby) have a built-in .plot() method -- often the fastest way to chart a result you just calculated, without switching back to matplotlib's own syntax.

A few rules keep charts useful instead of decorative: always label your axes, always give the chart a clear title stating what it shows, and choose the right chart type for the question -- line charts for trends over time, bar charts for comparing categories, and scatter plots for the relationship between two numeric variables (plt.scatter(x, y)).

Resist the urge to add every possible data point to one chart. A chart that answers one clear question well is far more useful than one that tries to show everything at once.`,
          },
          {
            title: 'Telling a Story with Your Data',
            type: 'text',
            duration: 420,
            textContent:
`Analysis isn't finished when you have the right numbers -- it's finished when someone who wasn't in the weeds with you can understand and act on what you found. This last step is where a lot of good analysis gets wasted.

Lead with the finding, not the process. "Revenue in the South region dropped 18% in March, driven mainly by lower repeat orders" is far more useful up front than a step-by-step account of every line of code you wrote to get there. Save the methodology for anyone who asks.

Use one chart per key point. If you have three findings, that's usually three simple charts, not one crowded chart trying to carry all three.

Give context, not just numbers. "Sales are 5,000 units" means little on its own. "Sales are 5,000 units, up 12% from last month and above our 4,500 target" tells a complete story.

Be honest about limitations. If your data has gaps, a small sample size, or an unusual event that might skew results (a holiday, a system outage), say so. This builds trust and prevents decisions based on a misleading number.

End with a clear takeaway or recommendation where you can. "Sales dropped in the South" is an observation; "Sales dropped in the South, likely due to X -- recommend investigating Y" turns your analysis into something someone can actually act on.

The technical skills in this course -- pandas, groupby, matplotlib -- are what get you to the answer. Communicating it clearly is what makes the answer actually matter.`,
            quiz: {
              title: 'Visualization & Communication Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'Which chart type is best suited to comparing totals across categories, like regions?',
                  options: ['Line chart', 'Bar chart', 'Scatter plot', 'Pie of pies'],
                  correctAnswer: 1,
                  explanation: 'Bar charts are well suited to comparing discrete categories against each other.',
                },
                {
                  questionText: 'What should you lead with when presenting a data analysis to others?',
                  options: [
                    'Every line of code you wrote',
                    'The key finding, stated clearly up front',
                    'A list of every column in the dataset',
                    'An apology for any messy data',
                  ],
                  correctAnswer: 1,
                  explanation: 'Leading with the finding respects the audience\'s time and makes the analysis immediately useful.',
                },
              ],
            },
          },
        ],
      },
    ],
    finalQuiz: {
      title: 'Data Analysis with Python -- Final Assessment',
      passingScore: 70,
      questions: [
        {
          questionText: 'Which pandas method gives you summary statistics (mean, min, max) for numeric columns?',
          options: ['df.head()', 'df.describe()', 'df.columns', 'df.plot()'],
          correctAnswer: 1,
          explanation: 'df.describe() computes summary statistics for numeric columns in the DataFrame.',
        },
        {
          questionText: 'Which method removes rows containing missing values?',
          options: ['df.fillna()', 'df.dropna()', 'df.drop_duplicates()', 'df.groupby()'],
          correctAnswer: 1,
          explanation: 'dropna() removes rows (or columns) that contain missing values.',
        },
        {
          questionText: 'What is the typical pattern behind a groupby() analysis?',
          options: [
            'Sort the data, then filter it',
            'Group by a category, then aggregate a numeric column',
            'Remove all missing values',
            'Import matplotlib',
          ],
          correctAnswer: 1,
          explanation: 'groupby() splits data into groups by category, and an aggregation (sum, mean, etc.) summarizes each group.',
        },
        {
          questionText: 'When presenting findings to a non-technical audience, what should come first?',
          options: [
            'The full code used',
            'The key finding, stated clearly',
            'A list of every chart type available',
            'The dataset\'s file size',
          ],
          correctAnswer: 1,
          explanation: 'Leading with the finding makes the analysis immediately understandable and actionable.',
        },
      ],
    },
  },

  {
    id: 'freelance-starter-kit',
    title: 'Freelancing & Entrepreneurship Starter Kit',
    description: 'A practical guide to starting and running a freelance business or small venture: pricing, finding clients, contracts, and cash flow.',
    longDescription:
      "This course is for anyone starting to sell a skill or service independently -- design, writing, development, consulting, or any other freelance work. Rather than abstract business theory, it focuses on the concrete decisions you'll actually face in your first few months: what to charge, where to find clients, how to protect yourself with a simple contract, and how to manage money when income is irregular.",
    category: 'business',
    level: 'Beginner',
    duration: '3 weeks',
    isPaid: false,
    price: 0,
    thumbnail: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1000&q=60',
    whatYouLearn: [
      'Define a clear service offering and niche',
      'Price your work with confidence, without guessing',
      'Find and win your first clients',
      'Protect yourself with contracts and clear payment terms',
      'Manage cash flow when income is irregular',
    ],
    requirements: [
      'A skill or service you can offer (existing or being developed)',
      'No business background required',
    ],
    tags: ['freelancing', 'entrepreneurship', 'small business', 'pricing', 'self-employment'],
    sections: [
      {
        title: 'Getting Started as a Freelancer',
        lectures: [
          {
            title: 'Choosing a Service and Defining Your Niche',
            type: 'text',
            duration: 360,
            textContent:
`"I do a bit of everything" is one of the most common reasons new freelancers struggle to get hired. Clients hire specialists more readily than generalists, because a specialist looks like a lower-risk, more confident choice for their specific problem.

A niche isn't about permanently limiting yourself -- it's about being known for something specific enough that people think of you when that need comes up. "I design logos and brand identities for restaurants" is far more memorable and hireable than "I do graphic design."

To find a workable niche, look at the overlap between three things: what you're actually good at, what you enjoy enough to keep doing, and what people are willing to pay for. A skill nobody will pay for isn't a business yet, no matter how good you are at it; work you can do but dislike will burn you out even if it pays.

You don't need to have this perfectly figured out on day one. Start with a reasonable guess, take on a few projects, and pay attention to which type of work you enjoyed most, delivered best, and got the strongest client reactions to. Narrow in from there. Most freelancers' niches sharpen over their first year, not before it.`,
          },
          {
            title: 'Pricing Your Work Confidently',
            type: 'text',
            duration: 420,
            textContent:
`Pricing is where most new freelancers struggle most, usually by charging too little out of fear of losing the client.

There are three common pricing models. Hourly pricing (charge per hour worked) is simple and low-risk for you, but it caps your income to your hours and can make clients nervous about an open-ended bill. Project-based pricing (a fixed fee for a defined scope of work) is what most experienced freelancers move toward -- it rewards you for being efficient rather than penalizing you for it, and gives the client cost certainty. Retainer pricing (a fixed monthly fee for ongoing work or availability) provides predictable income and suits long-term client relationships.

Whichever model you use, your price needs to cover more than just your time: your living costs, taxes, equipment and software, time spent on unpaid work like admin and finding clients, and a margin for slow periods. A simple starting formula: figure out your desired monthly income, divide by your realistic billable hours per month (usually well under the total hours in a month, once you account for admin and finding work), and that gives you a baseline hourly rate -- even if you ultimately quote projects, not hours.

Never apologize for your price or announce a discount before being asked. If a client says your price is too high, it's a negotiation, not automatically a signal to cut your rate -- you can offer a smaller scope at a lower price instead of just lowering your rate for the same work.

As you gain experience, testimonials, and a portfolio, raise your prices for new clients regularly. Being fully booked at a low price is a sign to raise your rates, not a sign to keep taking on more work.`,
            quiz: {
              title: 'Getting Started Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'Why do clients often prefer specialists over generalists?',
                  options: [
                    'Specialists are always cheaper',
                    'A specialist feels like a lower-risk, more confident choice for a specific problem',
                    'Generalists are not allowed to freelance',
                    'Specialists work faster by law',
                  ],
                  correctAnswer: 1,
                  explanation: 'Specialization signals confidence and reduces perceived risk for the client\'s specific need.',
                },
                {
                  questionText: 'What should your price account for, beyond just the hours worked?',
                  options: [
                    'Only the client\'s budget',
                    'Living costs, taxes, equipment, unpaid admin time, and slow periods',
                    'Nothing else -- hourly time is the only factor',
                    'Only what competitors charge',
                  ],
                  correctAnswer: 1,
                  explanation: 'A sustainable price accounts for real costs and unpaid time, not just the hours directly billed.',
                },
              ],
            },
          },
        ],
      },
      {
        title: 'Finding and Keeping Clients',
        lectures: [
          {
            title: 'Where to Find Your First Clients',
            type: 'text',
            duration: 420,
            textContent:
`Your very first clients rarely come from cold strangers -- they usually come from your existing network, even if it doesn't feel like it yet.

Start by telling everyone you know -- friends, family, former colleagues, classmates -- exactly what you now do, in one clear sentence, and ask them to keep you in mind or pass your name along. Most people are happy to refer someone once they clearly understand what that person offers; the problem is usually that nobody explained it clearly enough to be remembered.

Show your work publicly, consistently. Sharing what you're working on (with permission, where relevant) on the platforms your target clients use builds visibility over time and is one of the most reliable long-term sources of inbound interest -- but it takes weeks or months to compound, so start it early rather than waiting until you "need" clients.

Reach out directly to a short, well-chosen list of potential clients rather than mass-messaging everyone you can find. A short, specific message referencing something real about their business, with a clear and modest ask, gets far better responses than a generic pitch sent to fifty strangers.

Consider one or two low-risk, low-cost first projects (even at a reduced rate) specifically to generate a testimonial and a portfolio piece, if you don't have either yet. Treat this as a deliberate, time-limited strategy to build proof, not as your permanent pricing.

Referrals from happy clients will eventually become your best and cheapest source of new work -- so treat every early client exceptionally well, and don't be shy about directly asking satisfied clients if they know anyone else who could use your help.`,
          },
          {
            title: 'Writing Proposals That Win Work',
            type: 'text',
            duration: 420,
            textContent:
`A proposal's job is to make saying "yes" feel easy and low-risk for the client -- not to impress them with everything you're capable of.

Start by clearly restating the client's problem in your own words, before you talk about yourself at all. This immediately signals that you listened and understood, which matters more to most clients than a long list of your credentials.

Describe your proposed approach in plain language -- what you'll actually do, step by step -- rather than vague promises like "high-quality results." Specificity builds confidence; vagueness raises doubt.

State a clear scope: exactly what is included, and just as importantly, what is not included. This single habit prevents the majority of scope disputes and awkward conversations later in the project.

Give one price (or a small number of clearly differentiated options), a timeline, and payment terms, all stated plainly. Ambiguity about money or deadlines is one of the most common sources of friction in freelance relationships, and it's entirely avoidable by being explicit upfront.

Keep the whole proposal short. A client deciding whether to hire you does not want to read three pages -- they want to quickly confirm that you understood their problem, know what you'll do about it, and can tell them what it costs. If a proposal can be a well-organized page, don't stretch it to five.`,
            quiz: {
              title: 'Finding Clients Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'Where do most freelancers\' first clients typically come from?',
                  options: [
                    'Cold outreach to strangers only',
                    'Their existing network and referrals',
                    'Paid advertising exclusively',
                    'Random luck',
                  ],
                  correctAnswer: 1,
                  explanation: 'Early work usually comes through people who already know you, even indirectly.',
                },
                {
                  questionText: 'What should a proposal state clearly to avoid later disputes?',
                  options: [
                    'Only the price',
                    'The scope: what is included and what is not',
                    'A list of unrelated past clients',
                    'Nothing -- keep it flexible on purpose',
                  ],
                  correctAnswer: 1,
                  explanation: 'A clear scope prevents misunderstandings about what work is and isn\'t covered.',
                },
              ],
            },
          },
        ],
      },
      {
        title: 'Running the Business Side',
        lectures: [
          {
            title: 'Contracts, Invoices, and Getting Paid',
            type: 'text',
            duration: 420,
            textContent:
`A contract doesn't need to be intimidating or drafted by a lawyer to be useful -- even a simple, clear written agreement protects both you and the client far better than a verbal understanding.

At minimum, a freelance contract or written agreement should cover: the scope of work (what will be delivered), the price and payment schedule (including what happens for late payment), the timeline, how many rounds of revisions are included (a very common source of scope creep if left unstated), and how either party can end the agreement early if needed.

For payment schedules, avoid 100% payment only on final delivery whenever possible. A common and reasonable structure is a portion upfront (e.g. 30-50%) before starting work, with the remainder on completion or in milestones for longer projects. An upfront payment also filters out clients who were never serious.

An invoice should clearly state: your business/name, the client's name, a description of the work, the amount due, the due date, and how to pay. Send it promptly on completion (or per your agreed schedule) rather than waiting -- delayed invoicing is one of the most common, entirely avoidable causes of delayed payment.

If a payment is late, a polite, direct follow-up referencing the original due date is usually enough. Keep a simple record of every invoice sent and its payment status, so nothing quietly falls through the cracks as your client list grows.`,
          },
          {
            title: 'Managing Cash Flow as a Freelancer',
            type: 'text',
            duration: 420,
            textContent:
`Freelance income is irregular by nature -- a strong month can be followed by a quiet one -- so managing cash flow matters just as much as earning the money in the first place.

Separate your business and personal money, even with something as simple as a second account. This makes it far easier to see whether the business itself is actually profitable, rather than mixing it in with personal spending and losing track.

Build a buffer before you need it. A common guideline is to aim for enough saved to cover 2-3 months of essential expenses, so a slow month or a late-paying client doesn't become an emergency. Build this gradually from stronger months rather than waiting for a "safe" moment that may not arrive on its own.

Track money owed to you (money you've invoiced but not yet received) separately from money already in hand. It's easy to feel financially comfortable because of invoices sent, then be caught short because none of that money has actually arrived yet.

Set aside money for taxes as income arrives, rather than only thinking about it at tax time. A simple habit -- moving a fixed percentage of every payment into a separate account the moment it's received -- prevents an unpleasant surprise later.

Finally, review your income and expenses monthly, even briefly. Freelancers who regularly look at these numbers catch problems (a client who consistently pays late, a service that costs more than it's worth) far earlier than those who only look when something already feels wrong.`,
            quiz: {
              title: 'Business Operations Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'Why is an upfront payment (before starting work) commonly recommended?',
                  options: [
                    'It is required by law',
                    'It reduces risk and filters out clients who were never serious',
                    'It means you never need an invoice',
                    'It guarantees the project will go smoothly',
                  ],
                  correctAnswer: 1,
                  explanation: 'An upfront payment reduces the freelancer\'s risk and signals genuine client commitment.',
                },
                {
                  questionText: 'What is a recommended cash buffer for irregular freelance income?',
                  options: [
                    'No buffer is needed',
                    'Enough to cover 2-3 months of essential expenses',
                    'One full year of expenses before starting at all',
                    'Exactly one month\'s rent only',
                  ],
                  correctAnswer: 1,
                  explanation: 'A 2-3 month buffer is a common guideline to absorb slow months or late payments.',
                },
              ],
            },
          },
        ],
      },
    ],
    finalQuiz: {
      title: 'Freelancing & Entrepreneurship -- Final Assessment',
      passingScore: 70,
      questions: [
        {
          questionText: 'Why does a clear niche generally help a new freelancer get hired?',
          options: [
            'It limits you permanently',
            'Clients see a specialist as a lower-risk, more confident choice',
            'It guarantees higher prices immediately',
            'It removes the need for a portfolio',
          ],
          correctAnswer: 1,
          explanation: 'Specialization builds client confidence and makes your offering more memorable.',
        },
        {
          questionText: 'What should a freelance price realistically account for?',
          options: [
            'Billable hours only',
            'Living costs, taxes, equipment, unpaid time, and slow periods',
            'Only what the client offers to pay',
            'Nothing beyond materials used',
          ],
          correctAnswer: 1,
          explanation: 'A sustainable rate covers more than direct billable time -- it factors in overhead and irregular income.',
        },
        {
          questionText: 'What is one of the most common sources of scope disputes in freelance work?',
          options: [
            'Charging too much upfront',
            'Not clearly stating what is included and excluded in the scope',
            'Sending invoices too early',
            'Having too many contracts',
          ],
          correctAnswer: 1,
          explanation: 'An unclear scope leaves room for disagreement about what work was actually agreed upon.',
        },
        {
          questionText: 'Why is separating business and personal money recommended?',
          options: [
            'It is required to legally freelance',
            'It makes it easier to see whether the business itself is profitable',
            'It automatically reduces taxes',
            'It has no real benefit',
          ],
          correctAnswer: 1,
          explanation: 'Separated accounts give a clearer, more honest view of the business\'s actual financial health.',
        },
      ],
    },
  },

  {
    id: 'international-trade-statistics',
    title: 'Understanding International Trade Statistics',
    description: 'Learn where trade statistics come from, how to use them for market research, and what limitations to watch for -- built on official ITC training material.',
    longDescription:
      "Trade statistics are one of the most accessible tools for understanding a market: who exports what, who imports it, and how fast a market is growing. This course walks through why trade advisers and exporters rely on trade statistics, where the data actually comes from (customs declarations and the UN's COMTRADE database), and the real limitations you need to know before trusting a number -- from mirror statistics to re-exports skewing a country's apparent trade profile.",
    category: 'business',
    level: 'Beginner',
    duration: '2 weeks',
    isPaid: false,
    price: 0,
    thumbnail: 'https://images.unsplash.com/photo-1494412651409-8963ce7935a7?auto=format&fit=crop&w=1000&q=60',
    whatYouLearn: [
      'Why trade statistics are a fast, low-cost starting point for market research',
      'Where national and international trade data actually comes from, including COMTRADE',
      'What "mirror statistics" are and when they have to be used instead of direct data',
      'The real limitations of trade statistics: incompleteness, re-exports, valuation, and exchange rates',
    ],
    requirements: [
      'No prior statistics or trade background required',
      'Useful for exporters, trade advisers, and anyone researching international markets',
    ],
    tags: ['trade statistics', 'international trade', 'market research', 'export', 'import', 'comtrade'],
    sections: [
      {
        title: 'Why Trade Statistics Matter',
        lectures: [
          {
            title: 'What Trade Statistics Can Tell You',
            type: 'slides',
            duration: 360,
            slides: [
              {
                title: 'How Can You Quickly Answer These Questions?',
                content:
`Who are the main importers in the world market?
Who are the main exporters in the world market?
What is the trade performance of your country in the world market?
Who are the fastest growing markets in the world market?
Which markets seem to be the most attractive for your country?
How is your country performing in attractive markets?

Trade statistics can help you understand what is happening in the world market.`,
                caption: 'Trade statistics answer the core questions behind any market research effort.',
                imageUrl: preSlide('Picture1.png'),
                order: 0,
              },
              {
                title: 'The Benefits of Trade Statistics (1)',
                content:
`International trade statistics are comprehensive in terms of:
- Product coverage
- Geographical coverage
- Time

They are also readily available at moderate cost, making them an attractive source for market research and trade performance assessments.

Trade statistics are far more detailed than domestic trade data. Because they are collected with a high level of international standardisation, analysis can shift smoothly from national markets to regional groupings, or even a global view.`,
                caption: 'Comprehensive, standardized, and available at moderate cost.',
                imageUrl: preSlide('Picture2.png'),
                order: 1,
              },
              {
                title: 'The Benefits of Trade Statistics (2)',
                content:
`For exporters, an analysis of trade statistics contributes particularly to:
- Pre-selecting new export markets (market screening)
- Scanning new sources of import supply
- Analysing trends in international demand and in target countries
- Assessing competition with exporters or importers from other countries

For Trade Promotion Organisations, trade statistics are useful for:
- Developing national trade development strategies and programmes
- Monitoring national trade performance
- Identifying priority products and markets for special trade promotion activities
- As an element of trade information services`,
                caption: 'Trade statistics drive both exporter market screening and national trade promotion strategy.',
                imageUrl: preSlide('Picture3.png'),
                order: 2,
              },
            ],
            quiz: {
              title: 'Why Trade Statistics Matter -- Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'Why are trade statistics considered a good starting point for market research?',
                  options: [
                    'They are the only data source available for any market',
                    'They are comprehensive, internationally standardized, and available at moderate cost',
                    'They provide detailed company-level sales data',
                    'They are updated in real time every hour',
                  ],
                  correctAnswer: 1,
                  explanation: 'Trade statistics are comprehensive in product and geographical coverage and available at moderate cost, which is what makes them a practical starting point.',
                },
                {
                  questionText: 'According to this lecture, efficient use of trade statistics requires...',
                  options: [
                    'Ignoring where the data comes from',
                    'A good understanding of the sources themselves and the concepts behind trade statistics',
                    'A paid subscription to a single provider',
                    'Only using data from the current year',
                  ],
                  correctAnswer: 1,
                  explanation: 'Trade statistics are only useful when you understand both the sources and the concepts behind them -- that context is what makes the numbers meaningful.',
                },
              ],
            },
          },
        ],
      },
      {
        title: 'Where Trade Data Comes From',
        lectures: [
          {
            title: 'From Customs Declarations to COMTRADE',
            type: 'slides',
            duration: 420,
            slides: [
              {
                title: 'Where Do National Trade Statistics Come From? (1)',
                content:
`Trade statistics are a by-product of customs procedures. They exist to ascertain the direction and commodities included in trade flows that have to be declared to customs authorities. This explains why comparable statistics do not exist for domestic trade (not even in large, diversified countries like the USA) nor for non-merchandise trade flows, such as services.

The raw data for the compilation of trade statistics comes from customs declarations filled in by exporters and importers and verified by customs authorities.

In countries where customs procedures are fully computerised, the customs department sends raw data files to the department of statistics, where the data is cleaned and aggregated by product and country, usually on a monthly basis.`,
                caption: 'National trade statistics start as customs declarations filed by exporters and importers.',
                imageUrl: preSlide('Picture6.png'),
                order: 0,
              },
              {
                title: 'Where Do National Trade Statistics Come From? (2)',
                content:
`National offices of statistics collect and process all customs declarations, then publish the data nationally and send it to international organisations that keep track of trade databases.

There is only ONE truly international source of foreign trade statistics: the COMTRADE database, maintained by the United Nations Statistics Division (UNSD). It lists the values and quantities of exports and imports of some 160 countries and territories, by product and by partner country.

ITC's own market analysis tools are built on COMTRADE data.`,
                caption: 'COMTRADE, maintained by the UN Statistics Division, is the one truly international trade data source.',
                imageUrl: preSlide('Picture7.png'),
                order: 1,
              },
              {
                title: 'What Happens When a Country Does Not Report to COMTRADE?',
                content:
`The trade of countries that do not report trade data to the United Nations can be reconstructed on the basis of data reported by their partner countries. Statistics obtained this way are called mirror statistics.

While mirror statistics are a second-best solution, they are better than having no data at all. However, they have shortcomings compared to nationally reported data:
- They do not cover trade with non-reporting countries -- as a result, mirror statistics do not adequately cover intra-Africa trade.
- They invert the reporting standards, valuing exports in CIF terms and imports in FOB terms.
- The number of reporting countries differs from year to year, so comparisons over time using mirror statistics need to be interpreted with caution.`,
                caption: 'Mirror statistics reconstruct a non-reporting country\'s trade from its partners\' data -- a second-best but useful option.',
                imageUrl: preSlide('Picture8.png'),
                order: 2,
              },
              {
                title: 'Direct Data vs. Mirror Data: An Example',
                content:
`United States (a reporting country) exports directly to Vietnam (a non-reporting country) -- this shows up as direct data: "US Exports, Direct data."

Because Vietnam does not report, its imports from the United States have to be reconstructed as mirror data: "Vietnam Imports, Mirror data."

Vietnam and North Korea are both non-reporting countries. Since neither reports trade data, and there is no third reporting partner to mirror it from, it becomes impossible to get trade data for this trade flow.`,
                caption: 'Mirror data only works when at least one side of the trade flow reports -- two non-reporters leaves a genuine data gap.',
                imageUrl: preSlide('Picture10.png'),
                order: 3,
              },
            ],
            quiz: {
              title: 'Where Trade Data Comes From -- Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'What is the only truly international source of foreign trade statistics?',
                  options: [
                    'A private market research firm\'s proprietary database',
                    'COMTRADE, maintained by the UN Statistics Division (UNSD)',
                    'Each country\'s central bank',
                    'A single global customs authority',
                  ],
                  correctAnswer: 1,
                  explanation: 'COMTRADE, maintained by the UNSD, lists exports and imports for around 160 countries and territories by product and partner.',
                },
                {
                  questionText: 'What are "mirror statistics"?',
                  options: [
                    'Trade data duplicated for backup purposes',
                    'A country\'s own customs declarations',
                    'Trade estimates for a non-reporting country, reconstructed from its partner countries\' reported data',
                    'Statistics that only cover services trade',
                  ],
                  correctAnswer: 2,
                  explanation: 'When a country does not report its trade data, its trade can be reconstructed from what its partner countries report about trading with it.',
                },
              ],
            },
          },
        ],
      },
      {
        title: 'Limitations & Careful Use',
        lectures: [
          {
            title: 'Problems With Trade Statistics',
            type: 'slides',
            duration: 360,
            slides: [
              {
                title: 'Problems With Trade Statistics',
                content:
`Despite the attractiveness of this comprehensive source of information, trade advisers should always remember its weak points:

- Trade data are never complete: smuggling and non-reporting are serious problems in a number of countries, and trade statistics, like any information source, are not free of mistakes and omissions.
- Most countries include re-imports in their import statistics and re-exports in their export statistics. A low-income country may show up as an exporter of airplanes simply because its national airline sold second-hand planes.
- The export value refers to the total or contract value, which can be very different from the local value-added. For many processing activities, the local value added is less than 20% of the export value.
- Exchange rate fluctuations are not always properly recorded. Values are normally aggregated over a one year period in local currency and converted into US dollars.`,
                caption: 'Trade data is never fully complete, and re-exports or exchange rates can distort what a number appears to show.',
                imageUrl: preSlide('Picture13.png'),
                order: 0,
              },
              {
                title: 'Using Foreign Trade Statistics Carefully',
                content:
`Before relying on foreign trade statistics as a basis for strategic market research, a trade adviser should always:
- Check the source of the information
- Confirm whether the data is based on direct or mirror data
- Account for possible discrepancies caused by non-completeness, re-imports and re-exports, and exchange rate fluctuations

Treat foreign trade statistics as a hazardous area for the unprepared -- powerful when used carefully, misleading when taken at face value.`,
                caption: 'Always check the source and whether data is direct or mirror data before acting on it.',
                imageUrl: preSlide('Picture17.png'),
                order: 1,
              },
            ],
            quiz: {
              title: 'Limitations & Careful Use -- Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'Why might a country appear to "export" a product it doesn\'t actually produce, such as airplanes?',
                  options: [
                    'Data entry errors are extremely common',
                    'Re-exports and re-imports are included in trade statistics, so resold goods can appear as exports',
                    'All countries report identical data',
                    'Exchange rates are fixed permanently',
                  ],
                  correctAnswer: 1,
                  explanation: 'Most countries include re-exports in their export statistics, so a resold second-hand product can make a country look like a producer of it.',
                },
                {
                  questionText: 'What should a trade adviser always check before relying on foreign trade statistics?',
                  options: [
                    'Only the total dollar value',
                    'The source of the data, and whether it is based on direct or mirror data',
                    'Nothing -- all trade data is equally reliable',
                    'The formatting of the report',
                  ],
                  correctAnswer: 1,
                  explanation: 'Checking the source and confirming direct vs. mirror data is the first step to interpreting trade statistics correctly.',
                },
              ],
            },
          },
        ],
      },
    ],
    finalQuiz: {
      title: 'Understanding International Trade Statistics -- Final Assessment',
      passingScore: 70,
      questions: [
        {
          questionText: 'What is the origin of national trade statistics?',
          options: [
            'Voluntary surveys of large exporters only',
            'Customs declarations filed by exporters and importers, verified by customs authorities',
            'Estimates published by international news agencies',
            'Central bank currency reserves reports',
          ],
          correctAnswer: 1,
          explanation: 'Trade statistics are a by-product of customs procedures, compiled from declarations filed by exporters and importers.',
        },
        {
          questionText: 'Which organization maintains COMTRADE?',
          options: [
            'The World Trade Organization',
            'The United Nations Statistics Division (UNSD)',
            'The International Trade Centre (ITC)',
            'The World Customs Organisation',
          ],
          correctAnswer: 1,
          explanation: 'COMTRADE is maintained by the UN Statistics Division and is the only truly international source of foreign trade statistics.',
        },
        {
          questionText: 'What is a key limitation of mirror statistics?',
          options: [
            'They are always more accurate than direct data',
            'They do not cover trade between two non-reporting countries',
            'They only exist for services trade',
            'They cannot be used for any market research',
          ],
          correctAnswer: 1,
          explanation: 'When neither side of a trade flow reports data, there is no partner data to mirror it from, leaving a genuine gap.',
        },
        {
          questionText: 'Why can trade statistics show a country "exporting" something it doesn\'t produce, like a low-income country exporting airplanes?',
          options: [
            'Because trade statistics only track raw materials',
            'Because re-exports of previously imported or used goods are counted in export statistics',
            'Because all countries manufacture all products',
            'Because airplanes are excluded from trade statistics',
          ],
          correctAnswer: 1,
          explanation: 'Re-exports (such as a national airline reselling used planes) are included in export statistics, which can create a misleading picture.',
        },
      ],
    },
  },

  {
    id: 'harmonised-system-classification',
    title: 'The Harmonised System (HS): Classifying Products for Global Trade',
    description: 'Understand the Harmonised System coding used by over 200 countries and economies to classify products, set tariffs, and compile trade statistics -- built on official ITC training material.',
    longDescription:
      "Every internationally traded product is assigned a Harmonised System (HS) code, whether or not the country involved is a member of the HS Convention. This course explains what the HS is, how its 2/4/6-digit structure classifies products by increasing detail, and why customs authorities, trade negotiators, and statisticians all rely on the same coding system. You'll also see how the HS itself came about, evolving from separate, incompatible national systems into the single classification structure used today.",
    category: 'business',
    level: 'Beginner',
    duration: '2 weeks',
    isPaid: false,
    price: 0,
    thumbnail: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=1000&q=60',
    whatYouLearn: [
      'What the Harmonised System (HS) is and how its 2/4/6-digit codes work',
      'Why customs authorities, trade negotiators, and statisticians all rely on the HS',
      'How to read an HS code, from broad chapter down to specific sub-heading',
      'The history of the HS and why it replaced earlier, incompatible classification systems',
    ],
    requirements: [
      'No prior trade or customs background required',
      'Useful alongside the "Understanding International Trade Statistics" course',
    ],
    tags: ['harmonised system', 'hs code', 'customs', 'classification', 'international trade', 'tariffs'],
    sections: [
      {
        title: 'What Is the HS and Why It Matters',
        lectures: [
          {
            title: 'Introducing the Harmonised System',
            type: 'slides',
            duration: 420,
            slides: [
              {
                title: 'The Harmonised System',
                content: 'Lecture 2 of this trade classification series introduces the Harmonised System (HS) -- the coding system behind how products are classified in international trade.',
                caption: 'The Harmonised System: an internationally standardized way to classify traded products.',
                imageUrl: preSlide('Picture18.png'),
                order: 0,
              },
              {
                title: 'Introduction',
                content:
`In this lecture we will cover the following:
- What is the Harmonised System (HS)?
- Why do we use the Harmonised System?
- The structure of the HS
- HS Revisions
- HS in Trade Map
- HS in Market Access Map`,
                caption: 'An overview of what this lecture on the Harmonised System covers.',
                imageUrl: preSlide('Picture19.png'),
                order: 1,
              },
              {
                title: 'What Is the Harmonised System?',
                content:
`The Harmonised System, or HS, is an internationally standardized coding system used by countries to describe products in a consistent way.

In the HS, products are consistently classified using a 2, 4, or 6-digit code, assigned according to the physical properties of a product. The first two digits of a product's code are common to a broad group of products (the chapter). The next two digits are common to a more restricted group (the heading). The final two digits add the level of detail to distinguish products (the sub-heading).`,
                caption: 'HS codes classify products in increasing detail: 2 digits (chapter), 4 digits (heading), 6 digits (sub-heading).',
                imageUrl: preSlide('Picture20.png'),
                order: 2,
              },
              {
                title: 'Example: Classifying Tea',
                content:
`Chapter 09 includes coffee, tea, mate and spices. Heading 09.02 covers tea specifically, and each sub-heading represents a more specific description of tea.

HS 2: 09 -- Coffee, tea, mate, spices
HS 4: 09.02 -- Tea
HS 6: 09.02.10 -- Green tea in packages not exceeding 3 kg
       09.02.20 -- Green tea in packages exceeding 3 kg
       09.02.30 -- Black tea in packages not exceeding 3 kg
       09.02.40 -- Black tea in packages exceeding 3 kg

The HS is used by more than 200 countries and economies, whether or not they are members of the HS Convention, and the coding system covers around 98% of internationally traded goods.`,
                caption: 'A single product like tea gets progressively more specific as its HS code grows from 2 to 6 digits.',
                imageUrl: preSlide('Picture21.png'),
                order: 3,
              },
            ],
            quiz: {
              title: 'What Is the HS -- Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'In the Harmonised System, what determines a product\'s code?',
                  options: [
                    'The country of origin',
                    'Its physical properties, classified in increasing detail across 2, 4, and 6 digits',
                    'Its market price',
                    'The year it was traded',
                  ],
                  correctAnswer: 1,
                  explanation: 'HS codes are assigned according to a product\'s physical properties, with each additional pair of digits adding more detail.',
                },
                {
                  questionText: 'In the HS code 09.02.10 (green tea in packages not exceeding 3kg), what does the first pair of digits (09) represent?',
                  options: [
                    'A single, very specific product',
                    'The broad product chapter -- coffee, tea, mate and spices',
                    'The country of destination',
                    'The customs duty rate',
                  ],
                  correctAnswer: 1,
                  explanation: 'The first two digits identify the broad chapter; later digits (heading, then sub-heading) narrow it down to a specific product.',
                },
              ],
            },
          },
        ],
      },
      {
        title: 'Why Countries Use the HS',
        lectures: [
          {
            title: 'Customs, Negotiations, and Beyond',
            type: 'slides',
            duration: 420,
            slides: [
              {
                title: 'Why Do Countries Use the HS?',
                content:
`Countries use the Harmonised System for three main groups of purposes:
1. Customs procedures
2. Trade negotiations
3. Other purposes unrelated to international trade`,
                caption: 'The HS serves customs procedures, trade negotiations, and several other purposes.',
                imageUrl: preSlide('Picture24.png'),
                order: 0,
              },
              {
                title: 'The HS in Customs Procedures',
                content:
`Customs procedures rely on the HS to:
- Levy duties
- Control quotas on imported goods
- Apply rules of origin

A single classification system helps harmonize customs and trade procedures, improving the efficiency of export and import procedures and reducing trade costs. It also reduces uncertainty for exporters, who can predict how their products will be classified and the corresponding amount of customs tariff that will be charged.`,
                caption: 'Customs authorities use the HS to levy duties, control quotas, and apply rules of origin.',
                imageUrl: preSlide('Picture25.png'),
                order: 1,
              },
              {
                title: 'The HS in Trade Negotiations',
                content:
`The HS Nomenclature serves as a basis for trade negotiations. Using common codes facilitates negotiations on tariff concessions, since negotiators can refer to a particular HS code that has a common meaning for everyone.`,
                caption: 'Shared HS codes give trade negotiators a common reference point for tariff concessions.',
                imageUrl: preSlide('Picture26.png'),
                order: 2,
              },
              {
                title: 'Other Purposes of the HS',
                content:
`The HS also serves purposes unrelated to international trade, such as:
- Imposition of internal taxes
- Monitoring of controlled goods (for instance, hazardous waste)
- Analytical and statistical purposes

A uniform commodity classification enables the analysis of how trade patterns evolve across countries, helping identify export opportunities. Since analytical needs vary, different product classification systems also exist alongside the HS.`,
                caption: 'Beyond trade, the HS also underpins internal taxation, controlled-goods monitoring, and trade statistics.',
                imageUrl: preSlide('Picture27.png'),
                order: 3,
              },
            ],
            quiz: {
              title: 'Why Countries Use the HS -- Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'Besides international trade, what is the HS also used for?',
                  options: [
                    'Nothing -- it is used exclusively for international trade',
                    'Imposition of internal taxes and monitoring of controlled goods like hazardous waste',
                    'Setting currency exchange rates',
                    'Issuing passports',
                  ],
                  correctAnswer: 1,
                  explanation: 'The HS also supports internal taxation, monitoring controlled goods, and analytical or statistical purposes.',
                },
                {
                  questionText: 'How does the HS help in trade negotiations?',
                  options: [
                    'It sets fixed prices for all goods',
                    'Common HS codes give negotiators a shared reference point for discussing tariff concessions',
                    'It removes the need for negotiations entirely',
                    'It only applies to negotiations between neighboring countries',
                  ],
                  correctAnswer: 1,
                  explanation: 'Because HS codes have a common meaning for everyone, negotiators can refer to a specific code when discussing tariff concessions.',
                },
              ],
            },
          },
        ],
      },
      {
        title: 'History & Adoption of the HS',
        lectures: [
          {
            title: 'How the HS Came to Be',
            type: 'slides',
            duration: 360,
            slides: [
              {
                title: 'History of the HS',
                content:
`The HS came into effect through the International Convention on the Harmonised Commodity Description and Coding System, or the HS Convention (adopted in 1983, entered into force in 1988).

"Each Contracting Party shall also make publicly available its import and export trade statistics in conformity with the six-digit codes of the Harmonised System" (Article 3 of the HS Convention).`,
                caption: 'The HS Convention was adopted in 1983 and entered into force in 1988.',
                imageUrl: preSlide('Picture31.png'),
                order: 0,
              },
              {
                title: 'HS Development',
                content:
`The idea of naming internationally traded goods using common rules predates 1988. In 1937, the League of Nations' Minimum List of Commodities for International Trade Statistics attempted an internationally unified classification. An improved version, the Standard International Trade Classification (SITC), was published in 1951 and used mainly for statistical purposes (some countries also used it for customs).

Other countries based their tariff nomenclatures on a different system, the Brussels Tariff Nomenclature (BTN), which appeared in 1955. Both systems were later revised: SITC became SITC Revised, then SITC Revision 2; BTN became the Customs Cooperation Council Nomenclature (CCCN).

Despite the revisions, the two classification systems remained substantially different. There was a clear need to harmonize customs procedures across countries, and since customs declarations remain the main source of trade data, it was more convenient to use a single classification system for both customs and statistics. CCCN was expanded into the Harmonised Description and Coding System, now maintained by the World Customs Organisation. SITC still exists today, but is mostly used for economic analysis.`,
                caption: 'The HS evolved from two separate, incompatible systems (SITC for statistics, BTN for customs) merging into one.',
                imageUrl: preSlide('Picture32.png'),
                order: 1,
              },
            ],
            quiz: {
              title: 'History & Adoption -- Check',
              passingScore: 70,
              questions: [
                {
                  questionText: 'When did the HS Convention enter into force?',
                  options: ['1937', '1951', '1988', '2001'],
                  correctAnswer: 2,
                  explanation: 'The HS Convention was adopted in 1983 and entered into force in 1988.',
                },
                {
                  questionText: 'What system did many countries use for customs tariffs before the HS, alongside SITC for statistics?',
                  options: [
                    'The Brussels Tariff Nomenclature (BTN)',
                    'The World Trade Index',
                    'The Universal Product Code',
                    'The ISO Country Code list',
                  ],
                  correctAnswer: 0,
                  explanation: 'The Brussels Tariff Nomenclature (BTN), which appeared in 1955, was later expanded into what became the Harmonised System.',
                },
              ],
            },
          },
        ],
      },
    ],
    finalQuiz: {
      title: 'The Harmonised System -- Final Assessment',
      passingScore: 70,
      questions: [
        {
          questionText: 'Approximately what share of internationally traded goods does the HS cover?',
          options: ['About half', 'Around 98%', 'About 10%', 'Exactly 100%, with no exceptions'],
          correctAnswer: 1,
          explanation: 'The HS coding system covers around 98% of internationally traded goods.',
        },
        {
          questionText: 'Is a country required to be a member of the HS Convention to use the HS?',
          options: [
            'Yes, only Convention members may use it',
            'No -- it is used by more than 200 countries and economies whether or not they are members',
            'Only G7 countries may use it',
            'Only European countries use it',
          ],
          correctAnswer: 1,
          explanation: 'The HS is used by over 200 countries and economies regardless of formal membership in the HS Convention.',
        },
        {
          questionText: 'What determines the final two digits (the sub-heading) of an HS code?',
          options: [
            'The country of destination',
            'The most specific level of detail distinguishing a product within its heading',
            'The customs officer\'s judgment',
            'The date of shipment',
          ],
          correctAnswer: 1,
          explanation: 'The final two digits of a 6-digit HS code add the level of detail needed to distinguish specific products, like green vs. black tea.',
        },
        {
          questionText: 'Why did the HS replace separate systems like SITC and BTN?',
          options: [
            'Because SITC and BTN were identical and redundant',
            'Because using one classification system for both customs and statistics reduces cost and uncertainty',
            'Because the United Nations banned the older systems',
            'Because BTN and SITC were only usable in one country each',
          ],
          correctAnswer: 1,
          explanation: 'Since customs declarations are the main source of trade data, using a single classification system for both customs and statistics was more convenient than maintaining two incompatible systems.',
        },
      ],
    },
  },
  dcicFoundationClassTemplate,
];
